import { createChannel, getSlots, setSlot } from '@/api/networkFixer'
import { fetchConnector } from '@/api/projects'
import { assignLastCascadePriority } from '@/helpers/channels/assignLastCascadePriority'
import { getMultichatChannelDef } from '@/helpers/gcb2/multichat/channelDefs'
import { syncProjectAfterChannelChange } from '@/helpers/gcb2/multichat/syncProjectAfterChannelChange'
import type { MultichatBootstrapChannel } from '@/helpers/gcb2/multichat/types'
import type { MultichatSlotLike } from '@/helpers/gcb2/multichat/resolveChannels'

type StoreLike = {
    dispatch: (type: string, payload?: unknown) => Promise<any> | any
    commit: (type: string, payload?: unknown) => void
    state?: any
    getters?: any
}

export type EnsureDhConnectorResult = {
    ok: boolean
    connectorId?: string
    message?: string
}

const RETRY_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 400

function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

async function withRetries<T>(
    label: string,
    run: () => Promise<T>,
    isOk: (value: T) => boolean,
    attempts = RETRY_ATTEMPTS
): Promise<T> {
    let last: T | undefined
    for (let attempt = 1; attempt <= attempts; attempt++) {
        try {
            last = await run()
            if (isOk(last)) return last
        } catch (error) {
            console.error(`Multichat ${label} attempt ${attempt}/${attempts} failed:`, error)
            last = undefined as any
        }
        if (attempt < attempts) {
            await sleep(RETRY_BASE_DELAY_MS * attempt)
        }
    }
    return last as T
}

function findConnectorInStore(store: StoreLike, connectorId: string): boolean {
    const byId = store.getters?.connectorById
    if (typeof byId === 'function') {
        return Boolean(byId(connectorId))
    }

    const projects = store.state?.projects
    if (!Array.isArray(projects)) return false

    return projects.some((project: any) =>
        (project?.connectors || []).some(
            (c: any) => String(c?.connectorId || c?.id || '') === String(connectorId)
        )
    )
}

async function deleteConnectorWithRetries(
    store: StoreLike,
    connectorId: string
): Promise<EnsureDhConnectorResult> {
    const result = await withRetries(
        'deleteConnector',
        async () => {
            await store.dispatch('project/deleteConnector', connectorId)
            return !findConnectorInStore(store, connectorId)
        },
        (ok) => ok === true
    )

    if (!result) {
        return { ok: false, message: 'Не удалось удалить текущий канал. Попробуйте ещё раз.' }
    }
    return { ok: true }
}

async function findSlotIdForConnector(
    projectId: string,
    connectorId: string
): Promise<string | null> {
    const { data, error } = await getSlots({ projectId })
    if (error || !Array.isArray(data)) return null
    const slot = (data as MultichatSlotLike[]).find(
        (item) => String(item?.channelConnectorId || '') === String(connectorId)
    )
    return slot?.id != null ? String(slot.id) : null
}

/** Wait until slot no longer references the deleted connector (detach confirmation). */
async function waitSlotDetached(
    projectId: string,
    slotId: string,
    oldConnectorId: string
): Promise<boolean> {
    const result = await withRetries(
        'detachSlot',
        async () => {
            const { data, error } = await getSlots({ projectId })
            if (error || !Array.isArray(data)) return false
            const slot = (data as MultichatSlotLike[]).find(
                (item) => String(item?.id || '') === String(slotId)
            )
            if (!slot) return false
            const bound = slot.channelConnectorId
            return bound == null || String(bound) !== String(oldConnectorId)
        },
        (ok) => ok === true
    )
    return Boolean(result)
}

/**
 * Creates a DH connector for Multichat QR connect when one does not exist yet,
 * or replaces an external provider connector with the target DH one in the same slot.
 */
export async function ensureMultichatDhConnector(options: {
    store: StoreLike
    projectId: string
    channel: MultichatBootstrapChannel
    slotId: string | null
    /** External reconnect: delete old connector and create DH in the same slot */
    replaceExternal?: boolean
}): Promise<EnsureDhConnectorResult> {
    const { store, projectId, channel, replaceExternal = false } = options

    if (channel.connectorId && !replaceExternal) {
        return { ok: true, connectorId: channel.connectorId }
    }

    const def = getMultichatChannelDef(channel.key)
    const connectorType = channel.connectorType || def?.connectorType
    // Always target DH provider (never keep external providerType on reconnect)
    const providerType = def?.dhProviderType || channel.providerType

    if (!connectorType || !providerType) {
        return { ok: false, message: 'Не удалось определить тип канала для подключения' }
    }

    let slotId = options.slotId
    const oldConnectorId = channel.connectorId

    try {
        if (replaceExternal) {
            if (!oldConnectorId) {
                return { ok: false, message: 'Не найден connector для переподключения' }
            }

            const occupiedSlotId = await findSlotIdForConnector(projectId, oldConnectorId)
            if (occupiedSlotId) {
                slotId = occupiedSlotId
            }

            if (!slotId) {
                return { ok: false, message: 'Не удалось определить слот для переподключения' }
            }

            const deleted = await deleteConnectorWithRetries(store, oldConnectorId)
            if (!deleted.ok) return deleted

            const detached = await waitSlotDetached(projectId, slotId, oldConnectorId)
            if (!detached) {
                return {
                    ok: false,
                    message: 'Канал удалён, но слот ещё не освободился. Попробуйте ещё раз.',
                }
            }
        }

        if (!slotId) {
            return { ok: false, message: 'Нет свободного слота для подключения канала' }
        }

        const connector = await store.dispatch('project/createConnector', {
            projectId,
            connectorType,
            providerType,
            domain: null,
            settings: {},
        })

        const addRes = await store.dispatch('project/addConnector', connector)
        if (addRes?.error) {
            return { ok: false, message: 'Ошибка при создании коннектора' }
        }

        const connectorId = connector?.connectorId as string | undefined
        if (!connectorId) {
            return { ok: false, message: 'Коннектор создан без идентификатора' }
        }

        const commitConnector = (payload: Record<string, any>) => {
            store.commit('updateConnector', {
                ...payload,
                projectId: payload.projectId || projectId,
            })
        }

        try {
            const { data: fetchedConnector, error } = await fetchConnector(connectorId)
            if (!error && fetchedConnector) {
                commitConnector(fetchedConnector)
            }
        } catch {
            // non-fatal: QR can still start with the created id
        }

        const channelRes = await createChannel({ connectorId })
        const createdChannelId =
            !channelRes.error && channelRes.data != null ? channelRes.data : null

        if (channelRes.error) {
            console.error('Multichat createChannel failed:', channelRes.error)
            await syncProjectAfterChannelChange(store, projectId)
            return { ok: true, connectorId }
        }

        if (createdChannelId != null) {
            store.commit('updateConnectorFields', {
                connectorId,
                fields: { channelId: createdChannelId },
            })
        }

        const slotRes = await withRetries(
            'setSlot',
            async () => setSlot({ slotId: slotId!, channelConnectorId: connectorId }),
            (res) => !res?.error
        )
        if (slotRes?.error) {
            console.error('Multichat setSlot failed:', slotRes.error)
            return {
                ok: false,
                message: 'Канал создан, но не удалось привязать его к слоту',
            }
        }

        let updatedConnector: any = null
        try {
            const { data: fetchedConnector } = await fetchConnector(connectorId)
            if (fetchedConnector) {
                updatedConnector = {
                    ...fetchedConnector,
                    projectId: fetchedConnector.projectId || projectId,
                    channelId: fetchedConnector.channelId || createdChannelId,
                }
                commitConnector(updatedConnector)
            } else if (createdChannelId != null) {
                updatedConnector = { connectorId, projectId, channelId: createdChannelId }
            }
        } catch {
            if (createdChannelId != null) {
                updatedConnector = { connectorId, projectId, channelId: createdChannelId }
            }
        }

        if (updatedConnector?.channelId) {
            await assignLastCascadePriority({
                projectId,
                channelId: updatedConnector.channelId,
                providerType,
                notify: (message) => {
                    store.dispatch('callNotify', message)
                },
            })
        } else {
            console.warn('Multichat: channelId missing after createChannel, priorities skipped')
        }

        await syncProjectAfterChannelChange(store, projectId)

        // byId can briefly lag — keep channelId from createChannel / fetchConnector
        if (createdChannelId != null || updatedConnector?.channelId) {
            try {
                const { data: fetchedConnector } = await fetchConnector(connectorId)
                if (fetchedConnector) {
                    commitConnector({
                        ...fetchedConnector,
                        channelId:
                            fetchedConnector.channelId ||
                            updatedConnector?.channelId ||
                            createdChannelId,
                    })
                } else {
                    store.commit('updateConnectorFields', {
                        connectorId,
                        fields: {
                            channelId: updatedConnector?.channelId || createdChannelId,
                        },
                    })
                }
            } catch {
                store.commit('updateConnectorFields', {
                    connectorId,
                    fields: {
                        channelId: updatedConnector?.channelId || createdChannelId,
                    },
                })
            }
        }

        return { ok: true, connectorId }
    } catch (error) {
        console.error('Multichat ensureDhConnector failed:', error)
        return { ok: false, message: 'Ошибка при создании канала' }
    }
}
